import DOMPurify from 'dompurify'

/**
 * Sanitizer for tenant-authored portal HTML (navbar, home, footer, custom_html).
 *
 * SE-20: this HTML used to be rendered as written, so a tenant admin (or
 * anyone who got that permission) could store script. Unlike `SafeHtml` it
 * keeps what portal templates need: `<button>`, inline `style`, `class`, `id`
 * and `data-*`. It strips script, style tags, iframes, objects, forms, svg,
 * every `on*` handler and javascript:/vbscript: URLs.
 *
 * The built-in templates used three inline handlers. They are rewritten to
 * `data-kc3-action` markers BEFORE sanitizing and run by `bindPortalActions`,
 * which only knows this fixed list, so stored HTML cannot run arbitrary code.
 */
const purify = typeof window !== 'undefined' ? DOMPurify(window) : null

if (purify) {
  purify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.hasAttribute('href') && node.getAttribute('target')) {
      node.setAttribute('rel', 'noopener noreferrer')
    }
  })
}

const FORBID_TAGS = [
  'script', 'style', 'iframe', 'frame', 'frameset', 'object', 'embed', 'applet',
  'form', 'input', 'select', 'textarea', 'link', 'meta', 'base', 'svg', 'math',
]
const FORBID_ATTR = ['srcdoc', 'formaction', 'action']

const OPEN_CHAT = "window.dispatchEvent(new CustomEvent('kc3:open-chat'))"
const CLOSE_DETAILS = "this.closest('details').removeAttribute('open')"
const SHADOW = /^this\.style\.boxShadow='([^'";<>]{1,120})'$/

function decode(v: string): string {
  return v.replace(/&#39;|&apos;/g, "'").replace(/&quot;/g, '"').replace(/&amp;/g, '&').trim()
}

/** Turn the known legacy inline handlers into inert data attributes. */
export function normalizeLegacyHandlers(html: string): string {
  return html
    .replace(/\sonclick="([^"]*)"/gi, (m, raw: string) => {
      const code = decode(raw).replace(/;\s*return false;?$/, '').replace(/;$/, '')
      if (code === OPEN_CHAT) return ' data-kc3-action="open-chat"'
      if (code === CLOSE_DETAILS) return ' data-kc3-action="close-details"'
      if (code === 'return false') return ''
      return m
    })
    .replace(/\sonmouseover="([^"]*)"/gi, (m, raw: string) => {
      const hit = SHADOW.exec(decode(raw))
      return hit ? ` data-kc3-hover-shadow="${hit[1]}"` : m
    })
    .replace(/\sonmouseout="([^"]*)"/gi, (m, raw: string) => {
      const hit = SHADOW.exec(decode(raw))
      return hit ? ` data-kc3-rest-shadow="${hit[1]}"` : m
    })
}

export function sanitizePortalHtml(html: string | null | undefined): string {
  if (!html) return ''
  const normalized = normalizeLegacyHandlers(html)
  if (!purify) return normalized.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return purify.sanitize(normalized, {
    USE_PROFILES: { html: true },
    FORBID_TAGS,
    FORBID_ATTR,
    ADD_ATTR: ['target'],
    ALLOW_DATA_ATTR: true,
  })
}

/** Delegated behaviour for the fixed `data-kc3-*` markers. Returns a cleanup. */
export function bindPortalActions(container: HTMLElement): () => void {
  const onClick = (e: Event) => {
    const target = e.target as Element | null
    const actionEl = target?.closest<HTMLElement>('[data-kc3-action]')
    if (actionEl && container.contains(actionEl)) {
      const action = actionEl.getAttribute('data-kc3-action')
      if (action === 'open-chat') {
        window.dispatchEvent(new CustomEvent('kc3:open-chat'))
        if (actionEl.tagName === 'A') e.preventDefault()
      } else if (action === 'close-details') {
        actionEl.closest('details')?.removeAttribute('open')
      }
      return
    }
    const a = target?.closest('a')
    if (a && container.contains(a) && a.getAttribute('href') === '#') e.preventDefault()
  }
  const hover = (attr: string) => (e: Event) => {
    const el = (e.target as Element | null)?.closest<HTMLElement>(`[${attr}]`)
    if (el && container.contains(el)) el.style.boxShadow = el.getAttribute(attr) ?? ''
  }
  const onOver = hover('data-kc3-hover-shadow')
  const onOut = hover('data-kc3-rest-shadow')
  container.addEventListener('click', onClick)
  container.addEventListener('mouseover', onOver)
  container.addEventListener('mouseout', onOut)
  return () => {
    container.removeEventListener('click', onClick)
    container.removeEventListener('mouseover', onOver)
    container.removeEventListener('mouseout', onOut)
  }
}
